import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import type { StreakData, WeekDay } from '../types/streak';
import { useStreakData } from '../hooks/useStreakData';
import { useAwardBadges } from '../hooks/useAwardBadges';
import Lottie from 'lottie-react';
import fireAnimation from '@/assets/animations/Fire.json';

const StreakPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if we're in onboarding mode
  const isOnboarding = location.pathname.startsWith('/onboarding/');
  
  // Check if user came from reading (XP claim page) - hide back button
  const hideBackButton = isOnboarding || location.state?.fromReading || document.referrer.includes('/learning/xp-claim');

  // Use the new hook to get real streak data
  const { currentStreak, maxStreak, totalDays, weeklyProgress, isLoading, error } = useStreakData({
    fallbackCurrentStreak: isOnboarding ? 1 : 0,
  });
  const awardBadgesMutation = useAwardBadges();

  // Helper function to convert numbers to Persian digits
  const toPersianDigits = (num: number | string): string => {
    return String(num).replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[parseInt(d, 10)]);
  };

  // Calculate animation delays for completed/today/freeze days
  const getAnimationDelay = (index: number): number => {
    // Get indices of days that should be animated (completed, today, or freeze)
    const animatedDayIndices = weeklyProgress
      .map((day, idx) => (day.isCompleted || day.isToday || day.isFreeze) ? idx : -1)
      .filter(idx => idx !== -1);
    
    // Find the position of current day in the animated sequence
    const animationIndex = animatedDayIndices.indexOf(index);
    
    // If this day shouldn't be animated, return 0
    if (animationIndex === -1) return 0;
    
    // Return delay based on position in animated sequence
    return 0.35 + animationIndex * 0.12;
  };



  const handleCommitment = async () => {
    if (awardBadgesMutation.isPending) return; // prevent double submit
    
    // در حالت onboarding به phone برو
    if (isOnboarding) {
      navigate('/onboarding/phone', { replace: true });
      return;
    }
    
    // Get state from location (passed from XPClaimPage)
    const state = (location.state || {}) as any;
    
    // Check if we should show GoalsRewardPage
    // Show daily goal box if previous_minutes < daily_goal
    const showDailyGoalBox = state.previous_minutes != null && state.daily_goal != null && 
      state.previous_minutes < state.daily_goal;
    
    // Show streak goal box if previous_streak < streak_goal
    const showStreakGoalBox = state.previous_streak != null && state.streak_goal != null &&
      state.previous_streak < state.streak_goal;
    
    if (showDailyGoalBox || showStreakGoalBox) {
      // Navigate to GoalsRewardPage with all the state
      navigate('/learning/goals-reward', { state, replace: true });
      return;
    }
    
    // Otherwise, check for badges and navigate
    try {
      const awardResult = await awardBadgesMutation.mutateAsync();
      if (awardResult?.count > 0 && awardResult?.newBadges?.length > 0) {
        navigate('/learning/achievement', { state: { awardResult }, replace: true });
      } else {
        navigate('/learning/challenges', { replace: true });
      }
    } catch (e) {
      navigate('/learning/challenges', { replace: true });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-400 mx-auto mb-4"></div>
          <p className="text-gray-800 font-persian">در حال بارگذاری...</p>
        </div>
      </div>
    );
  }

  if (error && !isOnboarding) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 font-persian">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-[100dvh] bg-white text-gray-800 overflow-x-hidden flex flex-col" 
      style={isOnboarding
        ? { paddingTop: 'calc(var(--onboarding-header-h, 128px) - 4px)', paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom))' }
        : { paddingTop: '0.5rem', paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom))' }}
      dir="rtl"
    >
      <div className="container mx-auto px-4 max-w-md flex flex-1 flex-col w-full">
        {/* Header */}
        {!isOnboarding && (
          <div className="flex items-center justify-between mb-2">
            {!hideBackButton ? (
              <button
                onClick={() => navigate('/dashboard', { replace: true })}
                className="w-10 h-10 bg-white/80 rounded-full flex items-center justify-center hover:bg-white transition-all duration-200 hover:scale-110 shadow-sm"
              >
                <ArrowLeft className="w-5 h-5 rotate-180 text-gray-600" />
              </button>
            ) : (
              <div className="w-10 h-10"></div>
            )}
            
            <div className="w-10 h-10"></div>
          </div>
        )}

        <div className="flex flex-1 flex-col justify-center translate-y-4">
          {/* Fire Animation and Streak Counter */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.45, type: "spring", bounce: 0.25 }}
            className={`text-center mb-10 mt-0 ${isOnboarding ? 'scale-90' : ''}`}
          >
            <div className={`relative inline-block ${isOnboarding ? 'w-[10.5rem] h-[10.5rem]' : 'w-52 h-52'}`}>
              <Lottie 
                animationData={fireAnimation} 
                loop={true}
                speed={2.2}
                className="w-full h-full"
              />
            </div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.3 }}
              className="mt-10 flex flex-col items-center gap-10"
            >
              <h1 className={`!m-0 !h-auto !p-0 font-bold !leading-[0.82] text-gray-800 font-persian ${isOnboarding ? 'text-5xl' : 'text-6xl'}`}>
                {toPersianDigits(currentStreak)}
              </h1>
              <p className={`!m-0 !mt-0 !p-0 text-orange-400 font-semibold font-persian !leading-none ${isOnboarding ? 'text-lg' : 'text-xl'}`}>
                روز پیوستگی!
              </p>
            </motion.div>
          </motion.div>

          {/* Weekly Calendar */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.35 }}
            className={`rounded-3xl bg-white ring-1 ring-black/10 ${isOnboarding ? 'p-3 mb-3' : 'p-3 sm:p-4 mb-4'}`}
            style={{ boxShadow: '0 0 6px 1px rgba(0, 0, 0, 0.16)' }}
          >
            <div className="flex justify-between items-center mb-2">
            {weeklyProgress.map((day, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.45 + index * 0.05, duration: 0.22 }}
                className="flex flex-col items-center"
              >
                <span className={`text-gray-500 mb-1 font-persian ${isOnboarding ? 'text-xs' : 'text-sm'}`}>{day.day}</span>
                <div className={`rounded-full flex items-center justify-center text-sm font-bold border-2 bg-white border-gray-300 relative overflow-hidden ${isOnboarding ? 'w-[1.875rem] h-[1.875rem]' : 'w-8 h-8'}`}>
                  {(day.isCompleted || day.isToday) && (
                    <>
                      {/* Background fill animation - Orange for completed */}
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ 
                          delay: getAnimationDelay(index), 
                          duration: 0.6,
                          type: "spring",
                          bounce: 0.3
                        }}
                        className="absolute inset-0 bg-orange-400 rounded-full"
                      />
                      {/* Checkmark */}
                      <motion.div
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ 
                          delay: getAnimationDelay(index), 
                          duration: 0.6,
                          type: "spring",
                          bounce: 0.3
                        }}
                        className="text-white font-bold relative z-10"
                      >
                        ✓
                      </motion.div>
                    </>
                  )}
                  {day.isFreeze && !day.isCompleted && !day.isToday && (
                    <>
                      {/* Background fill animation - Ice blue for freeze */}
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ 
                          delay: getAnimationDelay(index), 
                          duration: 0.6,
                          type: "spring",
                          bounce: 0.3
                        }}
                        className="absolute inset-0 bg-sky-300 rounded-full"
                      />
                      {/* Snowflake icon for freeze */}
                      <motion.div
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ 
                          delay: getAnimationDelay(index), 
                          duration: 0.6,
                          type: "spring",
                          bounce: 0.3
                        }}
                        className="text-white font-bold relative z-10 text-xs"
                      >
                        ❄
                      </motion.div>
                    </>
                  )}
                </div>
              </motion.div>
            ))}
            </div>
          </motion.div>
        {/* Commitment Button */}
        <motion.button
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.35 }}
          whileHover={awardBadgesMutation.isPending ? undefined : { scale: 1.02 }}
          whileTap={awardBadgesMutation.isPending ? undefined : { scale: 0.98 }}
          onClick={handleCommitment}
          disabled={awardBadgesMutation.isPending}
          className={`w-[84%] max-w-[350px] mx-auto py-3.5 rounded-xl text-center font-bold font-persian text-[15px] transition-all duration-300 active:scale-95 shadow-xl hover:shadow-2xl flex items-center justify-center gap-3 bg-[#6e61ff] text-white ${awardBadgesMutation.isPending ? 'cursor-not-allowed opacity-70' : 'hover:bg-[#5a4ed9]'}`}
        >
          {awardBadgesMutation.isPending && (
            <svg className="animate-spin h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          )}
          <span>{isOnboarding ? 'ادامه بده' : 'متعهد هستم'}</span>
        </motion.button>
        </div>

      </div>
    </div>
  );
};

export default StreakPage;

